import { FC } from 'react';
import { NumericFormat } from 'react-number-format';
import { TextField, TextFieldProps } from '@mui/material';

// MUI's FormControlProps types defaultValue as `unknown`, which is wider than
// react-number-format's expected `string | number | null | undefined`. Omitting
// it from the public interface prevents the conflict when restProps is spread
// onto NumericFormat. Callers use `value` for controlled inputs anyway.
type MuiTextFieldForNumericFormat = Omit<TextFieldProps, 'defaultValue'>;

export interface DecimalInputProps extends Omit<
  TextFieldProps,
  'type' | 'onChange' | 'helperText' | 'defaultValue'
> {
  decimalPlaces?: number;
  /**
   * Called with the coerced value after each accepted keystroke:
   *   - `number`  for any entered value  (3.5, 12, 0.45) — always positive
   *   - `null`    when the field is empty
   */
  onChange?: (value: number | null) => void;
  value: number | null;
  min?: number;
  max?: number;
  warningText?: string;
  validationText?: string;
}

/**
 * A TextField component for positive decimal number input backed by react-number-format.
 * Sanitization, decimal-place limiting, negative sign rejection, and mid-typing states
 * are handled internally by the library — no manual regex or event mutation needed.
 */
export const DecimalInput: FC<DecimalInputProps> = ({
  decimalPlaces = 2,
  onChange,
  value,
  min,
  max,
  warningText,
  validationText,
  error,
  ...textFieldProps
}) => {
  const { slotProps: existingSlotProps, ...restProps } = textFieldProps;
  const hasWarning = !!warningText;
  const displayHelperText = error
    ? validationText
    : hasWarning
      ? warningText
      : (validationText ?? ' ');

  return (
    <NumericFormat
      {...restProps}
      customInput={
        TextField as React.ComponentType<MuiTextFieldForNumericFormat>
      }
      value={value}
      decimalScale={decimalPlaces}
      allowNegative={false}
      inputMode='numeric'
      onValueChange={({ floatValue }) => {
        onChange?.(floatValue ?? null);
      }}
      error={error}
      helperText={displayHelperText}
      slotProps={{
        ...existingSlotProps,
        htmlInput: {
          min,
          max,
          ...existingSlotProps?.htmlInput,
        },
        formHelperText: {
          sx: hasWarning ? { color: 'warning.main' } : undefined,
          ...existingSlotProps?.formHelperText,
        },
      }}
    />
  );
};
